import { useEffect, useMemo, useState } from 'react';
import {
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  CircularProgress,
  Typography,
  IconButton,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { twMerge } from 'tailwind-merge';

// Redux
import { useDispatch } from 'react-redux';
import {
  setSelectedUnit,
  setIsApartmentView,
} from '@/features/project/projectSlice';

import {
  ActionItem,
  ActionItemCategory,
  Process,
  UserData,
  UserRef,
} from '@urbanmix-tech/shared-js';
import { saveActionItem, saveProcess } from '@/data';
import { convertPseudoDateToRealDate } from '@/helpers/common';

import { ITableCell, ITableRow } from '@/components/shared/Table/Table';
import { VirtualizedTable } from '@/components/shared/Table';

import { useAuth } from '@/shared/context/AuthProvider';
import { borderColorClassByCategory } from '../UnitTabs/Todo/TodoAccordion';

import { UsersAvatars } from '../shared/UserAvatar/UserAvatars';

import { ActionCategoryCircle } from '../UnitTabs/Todo/ActionCategoryCircle';
import { useBuildingModel } from '../../contexts/Building/BuildingContext';
import { TaskList } from './TasksList';
import type { RootState } from '@/store';
import { useSelector } from 'react-redux';
import { ApartmentData } from '@/processes/AnalyzeUnits';
import { TrackerProcessGroup } from './TrackerProcessGroup';

const oneDayGap = 1 * 24 * 60 * 60 * 1000;

export type ActionGroupProps = {
  process?: Process;
  processId?: string;
  category: ActionItemCategory;
  actionItems: ActionItem[];
  expanded?: boolean;
  // onChangeExpanded: (category: ActionItemCategory, expanded: boolean) => void;
  onClickActionItem(actionItem: ActionItem): void;
  onHoverItem?(actionItem?: ActionItem): void;
  unitId: string;
  onProcessClick?(process: Process): void;
  editMode?: boolean;
  selected?: boolean;
  onCreateProcess?(process: Process): void;
};

export function ActionGroup({
  process,
  processId,
  category,
  actionItems,
  expanded,
  // onChangeExpanded,
  onClickActionItem,
  onHoverItem,
  unitId,
  onProcessClick,
  editMode,
  selected,
  onCreateProcess,
}: ActionGroupProps) {
  // console.log('action group',unitId,process)
  if (process) {
    return (
      <TrackerProcessGroup
        process={process}
        category={category}
        actionItems={actionItems}
        expanded={expanded}
        onClickActionItem={onClickActionItem}
        onHoverItem={onHoverItem}
        unitId={unitId}
        onProcessClick={onProcessClick}
        editMode={editMode}
        selected={selected}
      />
    );
  }

  // Mount Redux dispatcher
  const dispatch = useDispatch();

  const apartmentData = useSelector(
    (state: RootState) => state.project.apartmentData
  ) as ApartmentData[];

  const projectVisualizer = useSelector(
    (state: RootState) => state.project.projectVisualizer
  );

  const hoveredApartmentId = useSelector(
    (state: RootState) => state.project.hoveredUnitId
  );

  const authContext = useAuth();
  const { usersData } = authContext;
  const currentUserId = authContext.auth.currentUser?.uid;
  const currentUser: UserData | null = currentUserId
    ? usersData[currentUserId]
    : null;

  const { selectUnit, setUnitOverlayData } = useBuildingModel();

  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);

  const userGroupTasksTypes = useMemo(() => {
    const result = {
      hasOwnerActiveTasks: false,
      hasSupervisorActiveTasks: false,
      hasSupervisorLateTasks: false,
      hasFollowersActiveTasks: false,
      hasAnyActiveItems: false,
      hasLateTasks: false,
    };
    actionItems.forEach((item) => {
      if (item.owner?.userId === currentUserId && item.status !== 'done') {
        result.hasOwnerActiveTasks = true;
      }
      if (item.supervisor?.userId === currentUserId && item.status !== 'done') {
        result.hasSupervisorActiveTasks = true;
      }
      const completedOn = convertPseudoDateToRealDate(item.completedOn);
      const dueDate = convertPseudoDateToRealDate(item.dueDate) || new Date();
      const now = new Date().getTime();

      if (completedOn && completedOn.getTime() > now - 3600) {
        result.hasOwnerActiveTasks = true;
        result.hasAnyActiveItems = true;
      }
      if (
        item.supervisor?.userId === currentUserId &&
        item.status !== 'done' &&
        dueDate < new Date()
      ) {
        result.hasSupervisorLateTasks = true;
      }
      if (
        item.followers &&
        item.followers.find((f) => f.userId === currentUserId) &&
        item.status !== 'done'
      ) {
        result.hasFollowersActiveTasks = true;
      }
      if (item.status !== 'done') {
        result.hasAnyActiveItems = true;
      }
      if (dueDate < new Date() && item.status === 'active') {
        result.hasLateTasks = true;
      }
    });
    return result;
  }, [actionItems, currentUserId]);

  const activeGroupUsers = useMemo(() => {
    const users = new Set<string>();
    actionItems.forEach((item) => {
      if (item.owner?.userId && item.status !== 'done') {
        users.add(item.owner.userId);
      }
    });
    return Array.from(users).map((userId) => {
      let email = usersData[userId]?.email ?? '';
      return {
        userId,
        email: email,
      };
    }) as UserRef[];
  }, [actionItems, usersData]);

  const [showExpanded, setShowExpanded] = useState<boolean>(expanded ?? false);
  const [hasActiveItems, setHasActiveItems] = useState<boolean>(false);

  const [hovering, setHovering] = useState<boolean>(false);
  const [isCreatingProcess, setIsCreatingProcess] = useState(false);

  const handleCreateProcess = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!actionItems.length) return;
    const first = actionItems[0];
    const categoryName = first.category ?? 'process';
    const processId = `${unitId}_${categoryName}_${Date.now()}`;
    const newProcess: Process = {
      id: processId,
      name: categoryName,
      description: '',
      type: categoryName.toLowerCase().replace(/\s+/g, '_'),
      unitId: first.unitId,
      projectId: first.projectId,
      buildingId: first.buildingId,
      status: 'start',
      webhook_url: undefined,
      webhook_secret: undefined,
      startedAt: new Date(),
      updatedAt: new Date(),
      completedAt: undefined,
      parameters: [],
      workflows: [],
    };
    setIsCreatingProcess(true);
    try {
      await saveProcess(newProcess);
      await Promise.all(
        actionItems.map((item) => saveActionItem({ ...item, processId }))
      );
      onCreateProcess?.(newProcess);
    } finally {
      setIsCreatingProcess(false);
    }
  };

  useEffect(() => {
    const expandedGroup =
      userGroupTasksTypes.hasOwnerActiveTasks ||
      userGroupTasksTypes.hasSupervisorLateTasks;
    setShowExpanded(true);
    setHasActiveItems(userGroupTasksTypes.hasAnyActiveItems);
    selectUnit(expandedGroup ? unitId : null);
  }, [
    selectUnit,
    unitId,
    userGroupTasksTypes.hasAnyActiveItems,
    userGroupTasksTypes.hasOwnerActiveTasks,
    userGroupTasksTypes.hasSupervisorLateTasks,
  ]);

  const { groupColor, groupIcon } = useMemo(() => {
    let color: any = {};
    let icon: string = '';
    if (userGroupTasksTypes.hasOwnerActiveTasks && currentUser) {
      icon = 'owner';
    }
    if (userGroupTasksTypes.hasLateTasks) {
      icon = 'late';
    }
    if (!userGroupTasksTypes.hasAnyActiveItems) {
      icon = 'done';
    }

    if (icon === '') {
      icon = 'waiting';
    }

    //ts-ignore
    let data = apartmentData?.find((item) => item.apartmentId === unitId);

    if (data) {
      color = projectVisualizer.getColor(data.value);
      // FIX: Removed excessive logging that spammed console for every unit render
    } else {
      color = null;
    }

    const colorClass = `!bg-[${color}]`;
    return {
      groupColor: { color, class: colorClass },
      groupIcon: icon,
    };
  }, [
    activeGroupUsers,
    currentUser,
    userGroupTasksTypes.hasAnyActiveItems,
    userGroupTasksTypes.hasLateTasks,
    userGroupTasksTypes.hasOwnerActiveTasks,
    projectVisualizer,
    apartmentData,
    unitId,
  ]);

  useEffect(() => {
    if (hoveredApartmentId == unitId) {
      setHovering(true);
    } else {
      setHovering(false);
    }
  }, [hoveredApartmentId]);

  return (
    <Accordion
      className={twMerge(
        '!m-0 rounded-lg bg-zinc-900 border border-white/5 mb-0 overflow-hidden',
        showExpanded ? 'mb-16' : ''
      )}
      style={groupColor?.color ? { borderColor: groupColor.color } : {}}
      expanded={showExpanded}
      onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        // e.stopPropagation();
      }}
      onChange={() => {
        setShowExpanded(!showExpanded);
        expanded = !expanded;
        // onChangeExpanded(key as string, !expanded);
      }}
      sx={{
        backgroundColor: 'transparent',
        boxShadow: 'none',
        '&:before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary
        className="!m-0 !min-h-0 overflow-hidden !px-0 pt-[12px]"
        sx={{
          '& .MuiAccordionSummary-content': { margin: '0 !important' },
        }}
      >
        <Stack
          direction="row"
          gap="22px"
          alignItems="center"
          className="flex pb-[16px]"
          sx={{ width: '100%' }}
        >
          <Button
            onClick={(e: React.MouseEvent) => {
              dispatch(setSelectedUnit({ apartmentId: unitId }));
              e.stopPropagation();
            }}
            className="!min-w-0 !p-0 !ml-[16px] !rounded-lg overflow-hidden border border-white/10 group"
            sx={{
              width: 'auto',
              height: 36,
            }}
          >
            <div className="flex items-center justify-center w-full h-full px-[12px] bg-zinc-800/70 gap-[8px] group-hover:bg-zinc-700/70 transition-colors">
              <div
                className={twMerge('w-[8px] h-[8px] rounded-full')}
                style={{
                  backgroundColor: groupColor.color || '#ccc',
                }}
              />
              <Typography
                variant="body2"
                className="font-bold text-white whitespace-nowrap"
              >
                {unitId}
              </Typography>
            </div>
          </Button>

          <ActionCategoryCircle
            category={category}
            icon={groupIcon}
            colorClass={groupColor.class}
            color={groupColor.color}
            grayed={!userGroupTasksTypes.hasOwnerActiveTasks}
          />

          <Stack
            direction="row"
            gap="8px"
            alignItems="center"
            className="flex-1"
          >
            {actionItems && actionItems.length > 0 ? (
              <Typography
                variant="h6"
                className="font-semibold leading-tight tracking-tight text-stone-200 capitalize"
              >
                {actionItems[actionItems.length - 1].category}
              </Typography>
            ) : null}

            {editMode && (
              <Button
                size="small"
                variant="outlined"
                onClick={handleCreateProcess}
                disabled={isCreatingProcess}
                className="!text-zinc-400"
              >
                {isCreatingProcess ? <CircularProgress size={14} /> : 'Create Process'}
              </Button>
            )}

            {!showExpanded && <UsersAvatars users={activeGroupUsers} />}
            {/* <Divider orientation="vertical" className="h-16 border-white" />
            <span className="text-sm tracking-tight">Overdue</span> */}
          </Stack>

          <IconButton
            size="small"
            className="!text-zinc-400 !mr-[16px]"
            onClick={(e) => {
              e.stopPropagation();
              setShowExpanded(!showExpanded);
            }}
          >
            {showExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Stack>
      </AccordionSummary>

      <AccordionDetails className="bg-zinc-900 p-0">
        <TaskList
          actionItems={actionItems}
          onClickActionItem={onClickActionItem}
          onHoverItem={(item) => {
            if (item) {
              setHovering(true);
            } else {
              setHovering(false);
            }
            onHoverItem && onHoverItem(item);
          }}
          editMode={editMode}
        />
      </AccordionDetails>
    </Accordion>
  );
}
